/**
 * Route subtree mounted at `/w/:slug/kb` from `App.tsx`. Pages are
 * code-split via `React.lazy` + `<Suspense>` so the knowledge bundle
 * doesn't bloat the initial dashboard load. Each page is wrapped in
 * the per-route `<PageErrorBoundary>` per NFR-Design § 5.
 */

import { lazy, Suspense, type ReactElement } from "react";
import { Navigate, Route } from "react-router";

import { RouteFallback } from "../components/route-fallback";
import { PageErrorBoundary } from "./lib/page-error-boundary";

const KnowledgeListPage = lazy(() =>
  import("./pages/KnowledgeListPage").then((m) => ({ default: m.KnowledgeListPage })),
);
const KnowledgeCreatePage = lazy(() =>
  import("./pages/KnowledgeCreatePage").then((m) => ({ default: m.KnowledgeCreatePage })),
);
const KnowledgeDetailPage = lazy(() =>
  import("./pages/KnowledgeDetailPage").then((m) => ({ default: m.KnowledgeDetailPage })),
);
const RetrievalPlaygroundPage = lazy(() =>
  import("./pages/RetrievalPlaygroundPage").then((m) => ({
    default: m.RetrievalPlaygroundPage,
  })),
);
const KnowledgeSettingsPage = lazy(() =>
  import("./pages/KnowledgeSettingsPage").then((m) => ({ default: m.KnowledgeSettingsPage })),
);

function wrap(node: ReactElement): ReactElement {
  return (
    <PageErrorBoundary>
      <Suspense fallback={<RouteFallback />}>{node}</Suspense>
    </PageErrorBoundary>
  );
}

export function knowledgeRoutes() {
  return (
    <>
      <Route path="kb" element={wrap(<KnowledgeListPage />)} />
      <Route path="kb/new" element={wrap(<KnowledgeCreatePage />)} />
      <Route path="kb/:kbId" element={wrap(<KnowledgeDetailPage />)} />
      <Route path="kb/:kbId/settings" element={wrap(<KnowledgeSettingsPage />)} />
      {/* Old edit URL — redirect to the dedicated settings page. */}
      <Route path="kb/:kbId/edit" element={<Navigate to="../settings" replace />} />
      <Route path="kb/:kbId/playground" element={wrap(<RetrievalPlaygroundPage />)} />
    </>
  );
}
